import { COMPLIANCE_FLOW_STEPS } from '@/utils/complianceFlow'
import { ApplyDetail } from '@/apis/openAccount/getUserInfo'
import { ComplianceAuditStatus } from '@/apis/complianceAccount'

// 状态码映射
const STATUS_MAP = {
    pending: [100, 200, 300],
    approving: [400, 410, 420, 430],
    rejected: [800, 900],
    success: [500],
    failed: [600, 700]
}

// 根据状态码获取状态类型
function getStatusTypeByCode(statusCode) {
    if (STATUS_MAP.pending.includes(statusCode)) return 'pending'
    if (STATUS_MAP.approving.includes(statusCode)) return 'approving'
    if (STATUS_MAP.rejected.includes(statusCode)) return 'rejected'
    if (STATUS_MAP.success.includes(statusCode)) return 'approved'
    if (STATUS_MAP.failed.includes(statusCode)) return 'failed'
    return 'pending'
}

const state = {
    // 基本信息
    initialized: false,
    currentStep: '',
    completedSteps: [],
    
    // 交易密码验证
    passwordVerified: false,
    session2: '',
    rndKey2: '',
    
    // 职业状况信息
    jobStatusInfo: {
        // 页面优先使用的字段（兼容普通开户）
        degree: '',
        employment: '',
        industry: '',
        position: '',
        workLife: '',
        investFundsSource: '',
        // 合规出入金的字段（向后兼容）
        educationValue: '',
        educationCode: '',
        employmentValue: '',
        employmentCode: '',
        industryValue: '',
        industryCode: '',
        positionValue: '',
        positionCode: '',
        workYearsValue: '',
        workYearsCode: '',
        // 公共字段
        companyName: '',
        companyAddress: '',
        officeAddress: '',
        financialStatus: '', // 债务状况
        investmentSources: [] // 投资来源
    },
    
    // 财务状态信息
    financeInfo: {
        fundsSource: '', // 资金来源
        annualIncome: '', // 全年收入
        netAsset: '', // 净资产
        currentAsset: '', // 可用于投资的净资产
        annualAverageInvestAmount: '', // 每年平均投资金额
        houseType: '' // 住宅类别
    },
    
    // 背景状况披露
    backgroundInfo: {
        politicalPerson: '',
        fatfMember: '',
        highRiskIndustry: '',
        otherRiskFactors: ''
    },

    // 补充信息
    supplementInfo: {
        fundSources: [],
        tradingFrequency: '',
        thirdPartyTransfer: '',
        moneyLaundering: '',
        spouseAccount: ''
    },
    
    // 签署信息
    signatureInfo: {
        signatureImage: '',
        signatureFile: '',
        signatureTime: '',
        customerName: ''
    },
    
    // 合规审核状态
    auditStatus: {
        status: 'pending', // pending, approved, rejected
        reviewDate: '',
        reviewComments: '',
        approver: '',
        statusCode: null,
        rawData: null // 存储完整的原始审核数据
    },

    // 合规出入金审核完整数据
    complianceAuditData: null
}

const getters = {
    // 基本状态
    isInitialized: state => state.initialized,
    getCurrentStep: state => state.currentStep,
    getCompletedSteps: state => state.completedSteps,
    
    // 进度计算
    getProgress: state => {
        const totalSteps = COMPLIANCE_FLOW_STEPS.length
        const completed = state.completedSteps.length
        return {
            completed,
            total: totalSteps,
            percentage: Math.round((completed / totalSteps) * 100)
        }
    },
    
    // 步骤状态检查
    isStepCompleted: state => stepName => {
        return state.completedSteps.includes(stepName)
    },
    
    isStepAccessible: (state, getters) => stepName => {
        const stepIndex = COMPLIANCE_FLOW_STEPS.findIndex(step => step.name === stepName)
        if (stepIndex === 0) return true // 第一步总是可访问的
        
        const previousStep = COMPLIANCE_FLOW_STEPS[stepIndex - 1]
        return getters.isStepCompleted(previousStep.name)
    },
    
    // 具体信息获取
    getJobStatusInfo: state => state.jobStatusInfo,
    getFinanceInfo: state => state.financeInfo,
    getSupplementInfo: state => state.supplementInfo,
    getBackgroundInfo: state => state.backgroundInfo,
    getSignatureInfo: state => state.signatureInfo,
    getAuditStatus: state => state.auditStatus,
    
    // 密码验证状态
    isPasswordVerified: state => state.passwordVerified,
    
    // 整体完成状态
    isProcessCompleted: state => {
        return state.completedSteps.length === COMPLIANCE_FLOW_STEPS.length
    },
    
    // 下一步信息
    getNextStep: state => {
        const nextStep = COMPLIANCE_FLOW_STEPS.find(step => !state.completedSteps.includes(step.name))
        return nextStep || null
    },

    // 合规出入金审核完整数据
    getComplianceAuditData: state => state.complianceAuditData,

    // 获取 pdfTemplateOssId
    getPdfTemplateOssId: state => state.complianceAuditData?.pdfTemplateOssId || null,

    // 获取 accountOpenFileOssId (开户申请表)
    getAccountOpenFileOssId: state => state.complianceAuditData?.accountOpenFileOssId || null
}

const mutations = {
    // 初始化
    SET_INITIALIZED(state, status) {
        state.initialized = status
    },
    
    // 步骤管理
    SET_CURRENT_STEP(state, stepName) {
        state.currentStep = stepName
    },
    
    ADD_COMPLETED_STEP(state, stepName) {
        if (!state.completedSteps.includes(stepName)) {
            state.completedSteps.push(stepName)
        }
    },
    
    REMOVE_COMPLETED_STEP(state, stepName) {
        const index = state.completedSteps.indexOf(stepName)
        if (index > -1) {
            state.completedSteps.splice(index, 1)
        }
    },
    
    RESET_COMPLETED_STEPS(state) {
        state.completedSteps = []
    },
    
    // 密码验证
    SET_PASSWORD_VERIFIED(state, status) {
        state.passwordVerified = status
    },
    
    SET_TRADE_SESSION(state, { session2, rndKey2 }) {
        state.session2 = session2
        state.rndKey2 = rndKey2
    },
    
    // 职业状况
    UPDATE_JOB_STATUS(state, jobStatusInfo) {
        // 先更新传入的字段
        state.jobStatusInfo = { ...state.jobStatusInfo, ...jobStatusInfo }

        // 确保两套字段保持同步
        const currentInfo = state.jobStatusInfo

        // 同步学历字段
        if (jobStatusInfo.hasOwnProperty('degree')) {
            currentInfo.educationCode = jobStatusInfo.degree
            currentInfo.educationValue = jobStatusInfo.degree
        }
        if (jobStatusInfo.hasOwnProperty('educationCode')) {
            currentInfo.degree = jobStatusInfo.educationCode
            currentInfo.educationValue = jobStatusInfo.educationCode
        }

        // 同步职业状态字段
        if (jobStatusInfo.hasOwnProperty('employment')) {
            currentInfo.employmentCode = jobStatusInfo.employment
            currentInfo.employmentValue = jobStatusInfo.employment
        }
        if (jobStatusInfo.hasOwnProperty('employmentCode')) {
            currentInfo.employment = jobStatusInfo.employmentCode
            currentInfo.employmentValue = jobStatusInfo.employmentCode
        }

        // 同步行业字段
        if (jobStatusInfo.hasOwnProperty('industry')) {
            currentInfo.industryCode = jobStatusInfo.industry
            currentInfo.industryValue = jobStatusInfo.industry
        }
        if (jobStatusInfo.hasOwnProperty('industryCode')) {
            currentInfo.industry = jobStatusInfo.industryCode
            currentInfo.industryValue = jobStatusInfo.industryCode
        }

        // 同步职位字段
        if (jobStatusInfo.hasOwnProperty('position')) {
            currentInfo.positionCode = jobStatusInfo.position
            currentInfo.positionValue = jobStatusInfo.position
        }
        if (jobStatusInfo.hasOwnProperty('positionCode')) {
            currentInfo.position = jobStatusInfo.positionCode
            currentInfo.positionValue = jobStatusInfo.positionCode
        }

        // 同步工作年限字段
        if (jobStatusInfo.hasOwnProperty('workLife')) {
            currentInfo.workYearsCode = jobStatusInfo.workLife
            currentInfo.workYearsValue = jobStatusInfo.workLife
        }
        if (jobStatusInfo.hasOwnProperty('workYearsCode')) {
            currentInfo.workLife = jobStatusInfo.workYearsCode
            currentInfo.workYearsValue = jobStatusInfo.workYearsCode
        }

        // 同步办公地址字段
        if (jobStatusInfo.hasOwnProperty('officeAddress')) {
            currentInfo.companyAddress = jobStatusInfo.officeAddress
        }
        if (jobStatusInfo.hasOwnProperty('companyAddress')) {
            currentInfo.officeAddress = jobStatusInfo.companyAddress
        }
    },

    // 设置职业状况信息（用于初始化默认值）
    setJobStatusInfo(state, jobStatusInfo) {
        state.jobStatusInfo = { ...state.jobStatusInfo, ...jobStatusInfo }
    },
    
    // 财务状态
    UPDATE_FINANCE_INFO(state, financeInfo) {
        console.log('UPDATE_FINANCE_INFO 被调用')
        console.log('当前 state.financeInfo:', state.financeInfo)
        console.log('传入的 financeInfo:', financeInfo)
        console.log('financeInfo type:', typeof financeInfo)

        if (financeInfo && typeof financeInfo === 'object') {
            state.financeInfo = { ...state.financeInfo, ...financeInfo }
        } else {
            console.error('financeInfo 不是对象，跳过更新:', financeInfo)
        }

        console.log('更新后的 state.financeInfo:', state.financeInfo)
    },

    // 设置财务信息（用于初始化默认值）
    setFinanceInfo(state, financeInfo) {
        if (financeInfo && typeof financeInfo === 'object') {
            state.financeInfo = { ...state.financeInfo, ...financeInfo }
        }
    },

    // 设置审核状态（用于初始化默认值）
    setAuditStatus(state, status) {
        state.auditStatus = { ...state.auditStatus, statusCode: status }
    },
    
    // 补充信息
    UPDATE_SUPPLEMENT_INFO(state, supplementInfo) {
        state.supplementInfo = { ...state.supplementInfo, ...supplementInfo }
    },
    
    // 背景状况
    UPDATE_BACKGROUND_INFO(state, backgroundInfo) {
        state.backgroundInfo = { ...state.backgroundInfo, ...backgroundInfo }
    },
    
    // 签署信息
    UPDATE_SIGNATURE_INFO(state, signatureInfo) {
        state.signatureInfo = { ...state.signatureInfo, ...signatureInfo }
    },
    
    // 审核状态
    UPDATE_AUDIT_STATUS(state, auditStatus) {
        state.auditStatus = { ...state.auditStatus, ...auditStatus }
    },

    // 存储合规出入金审核完整数据
    SET_COMPLIANCE_AUDIT_DATA(state, auditData) {
        state.complianceAuditData = auditData
    },
    
    // 重置所有数据
    RESET_ALL_DATA(state) {
        state.currentStep = ''
        state.completedSteps = []
        state.passwordVerified = false
        state.session2 = ''
        state.rndKey2 = ''
        state.jobStatusInfo = {
            educationValue: '',
            educationCode: '',
            employmentValue: '',
            employmentCode: '',
            companyName: '',
            companyAddress: '',
            industryValue: '',
            industryCode: '',
            positionValue: '',
            positionCode: '',
            workYearsValue: '',
            workYearsCode: ''
        }
        state.financeInfo = {
            fundsSource: '', // 资金来源
            annualIncome: '', // 全年收入
            netAsset: '', // 净资产
            currentAsset: '', // 可用于投资的净资产
            annualAverageInvestAmount: '', // 每年平均投资金额
            houseType: '' // 住宅类别
        }
        state.supplementInfo = {
            politicallyExposed: false,
            pepDetails: '',
            sourceOfFunds: '',
            expectedTransactionVolume: ''
        }
        state.backgroundInfo = {
            criminalRecord: false,
            criminalDetails: '',
            bankruptcyRecord: false,
            bankruptcyDetails: '',
            regulatoryAction: false,
            regulatoryDetails: ''
        }
        state.signatureInfo = {
            signature: '',
            signatureDate: '',
            ipAddress: '',
            deviceInfo: ''
        }
        state.auditStatus = {
            status: 'pending',
            reviewDate: '',
            reviewComments: '',
            approver: ''
        }
    }
}

const actions = {
    // 初始化账户
    async initializeAccount({ commit, state, rootState }) {
        if (state.initialized) {
            return
        }

        try {
            console.log('开始初始化合规开户账户...')

            // 1. 调用 ApplyDetail 接口获取最新的开户信息（包括 appId）
            try {
                const { data } = await ApplyDetail()
                const result = data?.result

                if (result) {
                    console.log('ApplyDetail 返回结果:', result)

                    // 获取 appId 并存储
                    const appId = result.id || result.appId || 0
                    if (appId) {
                        console.log('从 ApplyDetail 获取到 appId:', appId)

                        // 存储到 localStorage（与开户流程保持一致）
                        localStorage.setItem('appId', appId)

                        // 更新到开户 store 中（合规流程复用开户 store）
                        if (rootState.account) {
                            commit('account/setStateData', { appId }, { root: true })
                            console.log('已更新 appId 到开户 store:', appId)
                        }
                    }

                    // 存储其他有用的信息
                    if (result.clientStatus) {
                        console.log('客户状态:', result.clientStatus)
                    }
                } else {
                    console.warn('ApplyDetail 接口未返回有效数据')
                }
            } catch (error) {
                console.error('调用 ApplyDetail 接口失败:', error)
                // 不阻断初始化流程，继续执行后续逻辑
            }

            // 2. 调用 ComplianceAuditStatus 接口获取合规审核状态和数据
            try {
                const { data } = await ComplianceAuditStatus()
                const result = data?.result

                if (result) {
                    console.log('ComplianceAuditStatus 返回结果:', result)

                    // 如果返回了职业信息，设置为默认值
                    if (result.employmentInfo) {
                        console.log('设置职业信息默认值:', result.employmentInfo)
                        commit('setJobStatusInfo', {
                            // 设置页面优先使用的字段（兼容普通开户的字段名）
                            degree: result.employmentInfo.degree || '',
                            employment: result.employmentInfo.employment || '',
                            industry: result.employmentInfo.industry || '',
                            position: result.employmentInfo.position || '',
                            workLife: result.employmentInfo.workLife || '',
                            investFundsSource: result.employmentInfo.investFundsSource || '',
                            // 同时设置合规出入金的字段名（向后兼容）
                            educationValue: result.employmentInfo.degree || '',
                            educationCode: result.employmentInfo.degree || '',
                            employmentValue: result.employmentInfo.employment || '',
                            employmentCode: result.employmentInfo.employment || '',
                            industryValue: result.employmentInfo.industry || '',
                            industryCode: result.employmentInfo.industry || '',
                            positionValue: result.employmentInfo.position || '',
                            positionCode: result.employmentInfo.position || '',
                            workYearsValue: result.employmentInfo.workLife || '',
                            workYearsCode: result.employmentInfo.workLife || '',
                            // 公共字段
                            companyName: result.employmentInfo.companyName || '',
                            companyAddress: result.employmentInfo.officeAddress || '',
                            officeAddress: result.employmentInfo.officeAddress || '',
                            financialStatus: result.employmentInfo.financialStatus || '',
                            investmentSources: result.employmentInfo.investFundsSource ? [result.employmentInfo.investFundsSource] : []
                        })
                    }

                    // 如果返回了财务信息，设置为默认值
                    if (result.financialInfo) {
                        console.log('设置财务信息默认值:', result.financialInfo)
                        commit('setFinanceInfo', {
                            fundsSource: result.financialInfo.fundsSource || '',
                            annualIncome: result.financialInfo.annualIncome || '',
                            netAsset: result.financialInfo.netAsset || '',
                            currentAsset: result.financialInfo.currentAsset || '',
                            annualAverageInvestAmount: result.financialInfo.annualAverageInvestAmount || '',
                            houseType: result.financialInfo.houseType || ''
                        })
                    }

                    // 如果返回了补充信息，设置为默认值
                    if (result.additionalInfo) {
                        console.log('设置补充信息默认值:', result.additionalInfo)
                        commit('UPDATE_SUPPLEMENT_INFO', {
                            fundSources: result.additionalInfo.fundSources || [],
                            tradingFrequency: result.additionalInfo.tradingFrequency || '',
                            thirdPartyTransfer: result.additionalInfo.thirdPartyTransfer || '',
                            moneyLaundering: result.additionalInfo.moneyLaundering || '',
                            spouseAccount: result.additionalInfo.spouseAccount || '',
                            spouseAccountNumbers: result.additionalInfo.spouseAccountNumbers || ['', '', '']
                        })
                    }

                    // 存储审核状态
                    if (result.status) {
                        commit('setAuditStatus', result.status)
                        console.log('设置审核状态:', result.status)
                    }
                } else {
                    console.warn('ComplianceAuditStatus 接口未返回有效数据')
                }
            } catch (error) {
                console.error('调用 ComplianceAuditStatus 接口失败:', error)
                // 不阻断初始化流程，继续执行后续逻辑
            }

            // 3. 从localStorage恢复合规流程数据
            const savedData = localStorage.getItem('complianceAccountData')
            if (savedData) {
                const data = JSON.parse(savedData)
                
                commit('SET_CURRENT_STEP', data.currentStep || '')
                commit('RESET_COMPLETED_STEPS')
                data.completedSteps?.forEach(step => {
                    commit('ADD_COMPLETED_STEP', step)
                })
                
                // 只有当 localStorage 中有有效数据时才覆盖接口数据
                // 检查 jobStatusInfo 是否有有效数据（不是空对象）
                if (data.jobStatusInfo && Object.keys(data.jobStatusInfo).length > 0) {
                    const hasValidData = Object.values(data.jobStatusInfo).some(value =>
                        value !== '' && value !== null && value !== undefined &&
                        (Array.isArray(value) ? value.length > 0 : true)
                    )
                    if (hasValidData) {
                        console.log('从 localStorage 恢复 jobStatusInfo:', data.jobStatusInfo)
                        commit('UPDATE_JOB_STATUS', data.jobStatusInfo)
                    }
                }

                // 检查 financeInfo 是否有有效数据
                if (data.financeInfo && Object.keys(data.financeInfo).length > 0) {
                    const hasValidData = Object.values(data.financeInfo).some(value =>
                        value !== '' && value !== null && value !== undefined
                    )
                    if (hasValidData) {
                        console.log('从 localStorage 恢复 financeInfo:', data.financeInfo)
                        commit('UPDATE_FINANCE_INFO', data.financeInfo)
                    }
                }

                // 检查 supplementInfo 是否有有效数据
                if (data.supplementInfo && Object.keys(data.supplementInfo).length > 0) {
                    const hasValidData = Object.values(data.supplementInfo).some(value =>
                        value !== '' && value !== null && value !== undefined &&
                        (Array.isArray(value) ? value.length > 0 : true)
                    )
                    if (hasValidData) {
                        console.log('从 localStorage 恢复 supplementInfo:', data.supplementInfo)
                        commit('UPDATE_SUPPLEMENT_INFO', data.supplementInfo)
                    }
                }

                // 检查 backgroundInfo 是否有有效数据
                if (data.backgroundInfo && Object.keys(data.backgroundInfo).length > 0) {
                    const hasValidData = Object.values(data.backgroundInfo).some(value =>
                        value !== '' && value !== null && value !== undefined
                    )
                    if (hasValidData) {
                        console.log('从 localStorage 恢复 backgroundInfo:', data.backgroundInfo)
                        commit('UPDATE_BACKGROUND_INFO', data.backgroundInfo)
                    }
                }

                // signatureInfo 和 auditStatus 可以直接恢复（这些通常不会从接口获取）
                if (data.signatureInfo) {
                    commit('UPDATE_SIGNATURE_INFO', data.signatureInfo)
                }
                if (data.auditStatus) {
                    commit('UPDATE_AUDIT_STATUS', data.auditStatus)
                }
                if (data.complianceAuditData) {
                    commit('SET_COMPLIANCE_AUDIT_DATA', data.complianceAuditData)
                }
            }
            
            // 检查交易密码验证状态
            const session2 = localStorage.getItem('session2')
            const rndKey2 = localStorage.getItem('rndKey2')
            if (session2 && rndKey2) {
                commit('SET_PASSWORD_VERIFIED', true)
                commit('SET_TRADE_SESSION', { session2, rndKey2 })
            }
            
            commit('SET_INITIALIZED', true)
            console.log('合规开户账户已初始化')
            
        } catch (error) {
            console.error('初始化合规开户账户失败:', error)
            throw error
        }
    },
    
    // 保存数据到localStorage
    saveToLocalStorage({ state }) {
        try {
            const dataToSave = {
                currentStep: state.currentStep,
                completedSteps: state.completedSteps,
                jobStatusInfo: state.jobStatusInfo,
                financeInfo: state.financeInfo,
                supplementInfo: state.supplementInfo,
                backgroundInfo: state.backgroundInfo,
                signatureInfo: state.signatureInfo,
                auditStatus: state.auditStatus,
                complianceAuditData: state.complianceAuditData
            }
            
            localStorage.setItem('complianceAccountData', JSON.stringify(dataToSave))
        } catch (error) {
            console.error('保存合规开户数据失败:', error)
        }
    },
    
    // 更新当前步骤
    updateCurrentStep({ commit, dispatch }, stepName) {
        commit('SET_CURRENT_STEP', stepName)
        dispatch('saveToLocalStorage')
    },
    
    // 标记步骤完成
    markStepCompleted({ commit, dispatch }, stepName) {
        commit('ADD_COMPLETED_STEP', stepName)
        dispatch('saveToLocalStorage')
    },
    
    // 取消步骤完成
    unmarkStepCompleted({ commit, dispatch }, stepName) {
        commit('REMOVE_COMPLETED_STEP', stepName)
        dispatch('saveToLocalStorage')
    },
    
    // 更新职业状况
    updateJobStatus({ commit, dispatch }, jobStatusInfo) {
        commit('UPDATE_JOB_STATUS', jobStatusInfo)
        dispatch('saveToLocalStorage')
    },
    
    // 更新财务信息
    updateFinanceInfo({ commit, dispatch }, financeInfo) {
        commit('UPDATE_FINANCE_INFO', financeInfo)
        dispatch('saveToLocalStorage')
    },
    
    // 更新补充信息
    updateSupplementInfo({ commit, dispatch }, supplementInfo) {
        commit('UPDATE_SUPPLEMENT_INFO', supplementInfo)
        dispatch('saveToLocalStorage')
    },
    
    // 更新背景信息
    updateBackgroundInfo({ commit, dispatch }, backgroundInfo) {
        commit('UPDATE_BACKGROUND_INFO', backgroundInfo)
        dispatch('saveToLocalStorage')
    },
    
    // 更新签署信息
    updateSignatureInfo({ commit, dispatch }, signatureInfo) {
        commit('UPDATE_SIGNATURE_INFO', signatureInfo)
        dispatch('saveToLocalStorage')
    },
    
    // 更新审核状态
    updateAuditStatus({ commit, dispatch }, auditStatus) {
        commit('UPDATE_AUDIT_STATUS', auditStatus)
        dispatch('saveToLocalStorage')
    },

    // 存储合规出入金审核状态数据
    async storeComplianceAuditData({ commit, dispatch }, auditData) {
        try {
            console.log('存储合规出入金审核状态数据:', auditData)

            // 存储完整的原始数据
            commit('SET_COMPLIANCE_AUDIT_DATA', auditData)

            // 存储审核状态信息
            if (auditData.status !== undefined) {
                const auditStatus = {
                    statusCode: auditData.status,
                    status: getStatusTypeByCode(auditData.status),
                    reviewDate: new Date().toISOString(),
                    reviewComments: auditData.reason || auditData.comment || '',
                    approver: auditData.approver || '',
                    rawData: auditData
                }
                commit('UPDATE_AUDIT_STATUS', auditStatus)
            }

            // 记录特殊字段
            if (auditData.pdfTemplateOssId) {
                console.log('存储 pdfTemplateOssId:', auditData.pdfTemplateOssId)
            }

            // 保存到 localStorage
            dispatch('saveToLocalStorage')

            console.log('合规出入金审核状态数据存储完成')
        } catch (error) {
            console.error('存储合规出入金审核状态数据失败:', error)
            throw error
        }
    },

    // 重置所有数据
    resetAllData({ commit }) {
        commit('RESET_ALL_DATA')
        commit('SET_INITIALIZED', false)
        localStorage.removeItem('complianceAccountData')
        sessionStorage.removeItem('complianceAuditFlag')
    }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}
